import React from 'react';
import Drawer from '../ui/Drawer';
import { NODE_DEFS } from '../../data/nodeDefinitions';
import styles from './NodeReportPanel.module.css';

function pct(n, d) {
  if (!d) return '—';
  return ((n / d) * 100).toFixed(1) + '%';
}

function Row({ label, value, sub, color }) {
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      <span className={styles.rowVal} style={color ? { color } : undefined}>
        {value}
        {sub && <span className={styles.rowSub}> {sub}</span>}
      </span>
    </div>
  );
}

// Per-node drill-down for Live View. Honest denominators: delivered÷sent is
// distinct from any goal metric; opens/clicks are overlapping engagement (both
// measured against sent), not mutually exclusive; untracked states show
// "Not tracked" rather than 0.
export default function NodeReportPanel({ open, node, metrics, onClose }) {
  if (!node) return null;
  const def = NODE_DEFS[node.type] || {};
  const m = metrics || {};
  const na = m.tAvail === false;
  const t = (v) => (na || v == null ? '—' : String(v));

  let body;
  if (m.kind === 'send') {
    body = (
      <>
        <div className={styles.section}>Flow</div>
        <Row label="Entered" value={t(m.entered)} />
        <Row label="Currently waiting" value={t(m.waiting)} color="var(--amberHi)" />
        <div className={styles.section}>Message funnel</div>
        <Row label="Sent (provider accepted)" value={m.sent} />
        <Row label="Delivered" value={m.delivered} sub={'· ' + pct(m.delivered, m.sent) + ' of sent'} color="var(--greenHi)" />
        <Row label="Opened / read" value={m.opened} sub={'· ' + pct(m.opened, m.sent) + ' of sent'} color="var(--blueHi)" />
        <Row label="Clicked" value={m.clicked} sub={'· ' + pct(m.clicked, m.sent) + ' of sent'} color="var(--purpleHi)" />
        <Row label="Failed" value={m.failed} color="var(--redHi)" />
        <div className={styles.section}>Not tracked yet</div>
        <Row label="Skipped / suppressed" value="Not tracked" />
        <Row label="Expired" value="Not tracked" />
        <div className={styles.note}>
          Provider acceptance (Sent) is not delivery. Opens and clicks overlap — both are measured against Sent, not each other. WhatsApp/Push have no delivery webhook yet, so Delivered may stay 0 for those channels.
        </div>
      </>
    );
  } else if (m.kind === 'branch') {
    const b = m.branches || {};
    body = (
      <>
        <div className={styles.section}>Split</div>
        <Row label="Entered" value={t(m.entered)} />
        <div className={styles.section}>Branches</div>
        {Object.keys(b).length && !na
          ? Object.entries(b).map(([k, v]) => (
              <Row key={k} label={k.toUpperCase()} value={v} sub={'· ' + pct(v, m.entered)} color="var(--purpleHi)" />
            ))
          : <Row label="Branch counts" value={na ? '—' : 'No data yet'} />}
      </>
    );
  } else if (m.kind === 'entry') {
    body = (
      <>
        <div className={styles.section}>Audience</div>
        <Row label="Admitted (entered step 1)" value={t(m.admitted)} />
        <Row label="Currently active" value={m.active} color="var(--blueHi)" />
        <Row label="Completed journey" value={m.completed} color="var(--greenHi)" />
        <div className={styles.section}>Not tracked yet</div>
        <Row label="Evaluated / excluded" value="Not tracked" />
        <div className={styles.note}>Admitted counts users who reached step 1. Per-run evaluated/excluded counts aren't captured yet.</div>
      </>
    );
  } else if (m.kind === 'exit') {
    body = (
      <>
        <div className={styles.section}>Exit</div>
        <Row label="Completed journey" value={m.completed} color="var(--greenHi)" />
      </>
    );
  } else {
    body = (
      <>
        <div className={styles.section}>Flow</div>
        <Row label="Entered" value={t(m.entered)} />
        <Row label="Currently waiting" value={t(m.waiting)} color="var(--amberHi)" />
      </>
    );
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={def.label}
      subtitle="Node report"
      ico={def.ico}
      icoStyle={{ background: def.icoC, color: def.icoT }}
      width="360px"
      footer={<button className={styles.doneBtn} onClick={onClose}>Done</button>}
    >
      <div className={styles.body}>
        {na && <div className={styles.unavailable}>Flow metrics (entered/waiting) are unavailable for this journey yet — they start recording from the next engine run after this deploy.</div>}
        {body}
      </div>
    </Drawer>
  );
}
